// The pass issued by the backend at sign-in.
//
// There is one pass per portal, not one per browser. The two portals are separate URLs (/admin and
// /supplier) but the same origin, so a single key meant that signing in as a supplier quietly replaced
// the admin's pass: the admin screens still looked signed in (ts_admin_logged_in was untouched) while
// every admin action came back "Admins only". Keying the pass by portal lets both be signed in at once,
// which is what anyone testing both sides, or checking what a supplier sees, actually needs.
const KEY = (role) => `ts_token_${role}`;
// The single pass everyone held before passes were split by portal. It is dropped rather than reused:
// nothing records which portal it belonged to, so handing a supplier's old pass to the admin screens
// would bring back the very "Admins only" refusal this change exists to remove. Everyone signs in once.
const LEGACY_KEY = 'ts_token';

// The shop this portal is signed in to, remembered beside the pass and keyed the same way. The server
// settles the shop from the pass, so this is never used to decide what may be read - it is what the
// browser keys its own snapshot and offline queue by, so two tabs signed in to two different shops do not
// share either.
const SHOP_KEY = (role) => `ts_shop_${role}`;
// Its name as well, so the screens can say which shop you are in without asking the server again on every
// load. The id is what keys anything stored; the name is only ever shown.
const SHOP_NAME_KEY = (role) => `ts_shop_name_${role}`;
// And what its prices are in, so every screen prints the right symbol without asking the server.
const SHOP_CURRENCY_KEY = (role) => `ts_shop_currency_${role}`;

// The portal decides which pass is used, and it is in the URL, so there is nothing to keep in sync.
export const currentRole = () =>
  window.location.pathname.toLowerCase().startsWith('/supplier') ? 'supplier' : 'admin';

export const getShop = (role) => {
  try { return localStorage.getItem(SHOP_KEY(role || currentRole())) || ''; } catch (e) { return ''; }
};

export const setShop = (id, role, name, currency) => {
  try {
    if (id) localStorage.setItem(SHOP_KEY(role || currentRole()), String(id));
    else localStorage.removeItem(SHOP_KEY(role || currentRole()));
    if (name) localStorage.setItem(SHOP_NAME_KEY(role || currentRole()), String(name));
    if (currency) localStorage.setItem(SHOP_CURRENCY_KEY(role || currentRole()), String(currency));
  } catch (e) { /* private window */ }
};

// Falls back to CNY, which is what every existing price in this install is in: a session signed in before
// the currency existed must not suddenly start printing dollars again.
export const getShopCurrency = (role) => {
  try { return localStorage.getItem(SHOP_CURRENCY_KEY(role || currentRole())) || 'CNY'; } catch (e) { return 'CNY'; }
};

export const getShopName = (role) => {
  try { return localStorage.getItem(SHOP_NAME_KEY(role || currentRole())) || ''; } catch (e) { return ''; }
};

export const getToken = (role) => {
  try {
    if (localStorage.getItem(LEGACY_KEY)) localStorage.removeItem(LEGACY_KEY);
    return localStorage.getItem(KEY(role || currentRole())) || '';
  } catch (e) { return ''; }
};

export const setToken = (t, role) => {
  if (!t) return;
  try {
    localStorage.setItem(KEY(role || currentRole()), t);
    localStorage.removeItem(LEGACY_KEY); // a pass from before passes were split
  } catch (e) { /* private window: this session simply will not be remembered */ }
};

export const clearToken = (role) => {
  try {
    localStorage.removeItem(KEY(role || currentRole()));
    localStorage.removeItem(SHOP_KEY(role || currentRole()));
    localStorage.removeItem(SHOP_NAME_KEY(role || currentRole()));
    localStorage.removeItem(SHOP_CURRENCY_KEY(role || currentRole()));
    localStorage.removeItem(LEGACY_KEY);
  } catch (e) {}
};

export function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// The backend refused the pass: drop it, along with the sign-in flag for this portal only, and tell
// App to show the login screen again. The other portal's session is none of its business.
export function endSession() {
  const role = currentRole();
  clearToken(role);
  try {
    localStorage.removeItem(role === 'supplier' ? 'ts_supplier' : 'ts_admin_logged_in');
  } catch (e) {}
  window.dispatchEvent(new Event('ts-session-expired'));
}
